import React from "react";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "./card";
import { Upload, CheckCircle, FileText } from "lucide-react";
import FileInput from "./FileInput";
import { MAX_FILE_SIZE_MB } from "../../utils/fileValidation";

interface DualFileInputProps {
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  accept?: string;
  frontDescription?: string;
  backDescription?: string;
  className?: string;
}

const DualFileInput: React.FC<DualFileInputProps> = ({
  onChange,
  accept = "image/*,.pdf",
  frontDescription = "Upload a clear image of the front side",
  backDescription = "Upload a clear image of the back side",
  className,
}) => {
  return (
    <Card
      className={cn(
        "w-full shadow-sm hover:shadow-md transition-shadow duration-300",
        className
      )}
    >
      <CardContent className="p-6 space-y-2">
        {/* Header Section */}
        <div className="space-y-2 text-center">
          <div className="flex items-center justify-center space-x-2">
            <div className="p-2 rounded-full bg-purple-50 ring-2 ring-purple-100">
              <Upload className="w-5 h-5 text-purple-600" />
            </div>
            <h3 className="text-sm font-semibold text-[#0f1627c4]">
              Upload Card Images
            </h3>
          </div>
          <p className="max-w-md mx-auto text-xs text-gray-500">
            Please upload both sides of your card. Supported formats include
            JPG, PNG, and PDF files.
          </p>
        </div>

        {/* File Input Grid */}
        <div className="grid gap-8 lg:grid-cols-2">
          <FileInput
            description={frontDescription}
            id="front-card"
            label={
              <span className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-purple-600" />
                Front of Card
              </span>
            }
            onChange={onChange}
            accept={accept}
            className="transition-transform duration-200 hover:scale-[1.02] hover:shadow-md p-2"
          />

          <FileInput
            accept={accept}
            description={backDescription}
            id="back-card"
            label={
              <span className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-purple-600" />
                Back of Card
              </span>
            }
            onChange={onChange}
            className="transition-transform duration-200 hover:scale-[1.02] hover:shadow-md p-2"
          />
        </div>

        {/* Footer Information */}
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <CheckCircle className="w-3 h-3 text-purple-600" />
            <span>Max size: {MAX_FILE_SIZE_MB}MB</span>
          </div>
          <div className="flex items-center gap-1">
            <CheckCircle className="w-3 h-3 text-purple-600" />
            <span>Secure upload</span>
          </div>
          <div className="flex items-center gap-1">
            <CheckCircle className="w-3 h-3 text-purple-600" />
            <span>Allowed formats: .png, .jpeg, .pdf</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DualFileInput;
